/**
 * Prompt 过滤管理页面
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  listFilters,
  createFilter,
  updateFilter,
  deleteFilter,
  batchDeleteFilters,
  FilterKeyword,
} from '../api/filters';
import { formatBeijingTime } from '../utils/time';

const { TextArea } = Input;
const { Option } = Select;

const PromptFilterPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<FilterKeyword[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingFilter, setEditingFilter] = useState<FilterKeyword | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [form] = Form.useForm();

  // 筛选条件
  const [language, setLanguage] = useState<string | undefined>();
  const [category, setCategory] = useState<string | undefined>();
  const [isActive, setIsActive] = useState<boolean | undefined>();
  const [search, setSearch] = useState<string>('');

  /**
   * 加载过滤关键词列表
   */
  const loadFilters = async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        page_size: pageSize,
      };
      
      // 只添加有值的参数
      if (language) params.language = language;
      if (category) params.category = category;
      if (isActive !== undefined) params.is_active = isActive;
      if (search && search.trim()) params.search = search.trim();
      
      console.log('Loading filters with params:', params);
      const response = await listFilters(params);
      console.log('Filters response:', response);
      if (response.success) {
        setFilters(response.data.keywords);
        setTotal(response.data.pagination.total);
        console.log('Loaded filters:', response.data.keywords.length, 'Total:', response.data.pagination.total);
      }
    } catch (error) {
      console.error('加载失败:', error);
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, language, category, isActive]);

  /**
   * 打开新增弹窗
   */
  const handleAdd = () => {
    setEditingFilter(null);
    form.resetFields();
    form.setFieldsValue({
      language: 'en',
      category: 'sexual',
      is_active: true,
    });
    setEditModalVisible(true);
  };

  /**
   * 打开编辑弹窗
   */
  const handleEdit = (record: FilterKeyword) => {
    setEditingFilter(record);
    form.setFieldsValue({
      keyword: record.keyword,
      language: record.language,
      category: record.category,
      is_active: record.is_active === 1,
      notes: record.notes,
    });
    setEditModalVisible(true);
  };

  /**
   * 保存
   */
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingFilter) {
        // 更新
        await updateFilter(editingFilter.id, values);
        message.success('更新成功');
      } else {
        // 新增
        await createFilter(values);
        message.success('添加成功');
      }
      
      setEditModalVisible(false);
      loadFilters();
    } catch (error: any) {
      console.error('保存失败:', error);
      message.error(error.response?.data?.message || '保存失败');
    }
  };

  /**
   * 删除
   */
  const handleDelete = async (id: number) => {
    try {
      await deleteFilter(id);
      message.success('删除成功');
      loadFilters();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  /**
   * 批量删除
   */
  const handleBatchDelete = async () => {
    try {
      const ids = selectedRowKeys.map(key => Number(key));
      const response = await batchDeleteFilters(ids);
      message.success(response.message);
      setSelectedRowKeys([]);
      loadFilters();
    } catch (error: any) {
      console.error('批量删除失败:', error);
      message.error(error.response?.data?.message || '批量删除失败');
    }
  };

  /**
   * 搜索
   */
  const handleSearch = () => {
    setPage(1);
    loadFilters();
  };

  /**
   * 表格列
   */
  const columns: ColumnsType<FilterKeyword> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '关键词',
      dataIndex: 'keyword',
      key: 'keyword',
      width: 200,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: '语言',
      dataIndex: 'language',
      key: 'language',
      width: 80,
      render: (lang: string) => {
        const colorMap: Record<string, string> = {
          en: 'blue',
          zh: 'green',
          other: 'default',
        };
        return <Tag color={colorMap[lang]}>{lang.toUpperCase()}</Tag>;
      },
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (cat: string) => {
        const colorMap: Record<string, string> = {
          sexual: 'red',
          violence: 'orange',
          hate: 'purple',
          general: 'default',
        };
        const nameMap: Record<string, string> = {
          sexual: '色情',
          violence: '暴力',
          hate: '仇恨',
          general: '通用',
        };
        return <Tag color={colorMap[cat]}>{nameMap[cat] || cat}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active: number) => (
        <Tag color={active ? 'green' : 'default'}>
          {active ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (time: string) => formatBeijingTime(time),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 筛选栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="搜索关键词"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 200 }}
          />
          <Select
            placeholder="语言"
            value={language}
            onChange={setLanguage}
            allowClear
            style={{ width: 120 }}
          >
            <Option value="en">英文</Option>
            <Option value="zh">中文</Option>
            <Option value="other">其他</Option>
          </Select>
          <Select
            placeholder="分类"
            value={category}
            onChange={setCategory}
            allowClear
            style={{ width: 120 }}
          >
            <Option value="sexual">色情</Option>
            <Option value="violence">暴力</Option>
            <Option value="hate">仇恨</Option>
            <Option value="general">通用</Option>
          </Select>
          <Select
            placeholder="状态"
            value={isActive}
            onChange={setIsActive}
            allowClear
            style={{ width: 120 }}
          >
            <Option value={true}>启用</Option>
            <Option value={false}>禁用</Option>
          </Select>
          <Button type="primary" icon={<FilterOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadFilters}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加关键词
          </Button>
          <Popconfirm
            title="确认批量删除"
            description={`确定要删除选中的 ${selectedRowKeys.length} 个关键词吗？`}
            onConfirm={handleBatchDelete}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            disabled={selectedRowKeys.length === 0}
          >
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              disabled={selectedRowKeys.length === 0}
            >
              批量删除 {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
            </Button>
          </Popconfirm>
        </Space>
      </Card>

      {/* 表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={filters}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
            selections: [
              Table.SELECTION_ALL,
              Table.SELECTION_INVERT,
              Table.SELECTION_NONE,
            ],
          }}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 编辑弹窗 */}
      <Modal
        title={editingFilter ? '编辑关键词' : '添加关键词'}
        open={editModalVisible}
        onOk={handleSave}
        onCancel={() => setEditModalVisible(false)}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="关键词"
            name="keyword"
            rules={[{ required: true, message: '请输入关键词' }]}
          >
            <Input placeholder="输入要过滤的关键词" />
          </Form.Item>
          <Form.Item
            label="语言"
            name="language"
            rules={[{ required: true, message: '请选择语言' }]}
          >
            <Select>
              <Option value="en">英文</Option>
              <Option value="zh">中文</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="分类"
            name="category"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select>
              <Option value="sexual">色情</Option>
              <Option value="violence">暴力</Option>
              <Option value="hate">仇恨</Option>
              <Option value="general">通用</Option>
            </Select>
          </Form.Item>
          <Form.Item label="状态" name="is_active" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
          <Form.Item label="备注" name="notes">
            <TextArea rows={3} placeholder="添加备注信息..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PromptFilterPage;
